import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Select, DatePicker, message } from 'antd';
import { convertLocalToUtcDate, formatDateTime } from 'library/time';
import { setLowerDateRange, setUpperDateRange } from 'library/helpers';
import REDUCER_KEY from 'reducers/reducer.keys';
import { load } from 'actions';
import { showValidationBlock } from 'actions/validationBlock';
import { Transfer } from 'components/Page';
import config from './config';

const mapStateToProps = state => ({
  tagValuesData: state.api.tagValuesForCollection ? state.api.tagValuesForCollection.data : { results: [] },
  tagValuesInFlight: state.api.tagValuesForCollection ? state.api.tagValuesForCollection.inFlight : false,
  tagValuesIsDirty: state.api.tagValues ? state.api.tagValues.isDirty : false
});

const mapDispatchToProps = dispatch => ({
  loadTagValues: collectionId => dispatch(
    load(REDUCER_KEY.tagValuesForCollection.key, { collectionId })
  ),
  dispatchShowValidationBlock: violations => dispatch(showValidationBlock(violations))
});

const TaggedItems = ({
  objectId, permissions, tagValuesData, tagValuesInFlight, tagValuesIsDirty, loadTagValues, dispatchShowValidationBlock
}) => {
  const [selectedTagId, setSelectedTagId] = useState(undefined);
  const [startDate, setStartDate] = useState(null);
  const [expiryDate, setExpiryDate] = useState(null);
  const canAdmin = permissions.CanAdminTags().hasPermissions();

  useEffect(() => {
    if (objectId) {
      loadTagValues(objectId);
      setSelectedTagId(undefined);
    }
  }, [objectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tagValuesIsDirty && objectId) {
      loadTagValues(objectId);
    }
  }, [tagValuesIsDirty]); // eslint-disable-line react-hooks/exhaustive-deps

  const tagValues = tagValuesData?.results || [];

  const handleAssignData = useCallback(userIds => ({
    users: userIds,
    start_date: startDate ? formatDateTime(convertLocalToUtcDate(startDate)) : null,
    expiry_date: expiryDate ? formatDateTime(convertLocalToUtcDate(expiryDate)) : null
  }), [startDate, expiryDate]);
  const handleUnassignData = useCallback(userIds => ({ users: userIds }), []);

  const handleAssignResponse = useCallback((response) => {
    if (response) {
      const { added } = response;
      if (added > 0) {
        message.success(`${added} user(s) tagged successfully`);
      }
 else {
        message.error('Failed to tag users');
      }
    }
  }, []);

  const handleUnassignResponse = useCallback((response) => {
    const payload = response?.data || response;
    if (response && response.status === 207 && payload && payload.blocked && payload.blocked.length) {
      dispatchShowValidationBlock(payload.blocked);
    }
    if (payload) {
      const { removed, skipped_not_assigned } = payload;
      if (removed > 0) {
        message.success('Tags are removed from the selected users');
      }
 else if (skipped_not_assigned > 0) {
        message.warning('No tags removed — selected users did not have this tag.');
      }
    }
  }, [dispatchShowValidationBlock]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '16px 24px', flexShrink: 0, borderBottom: '1px solid #e8e8e8' }}>
        <div style={{
          marginBottom: 0, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '16px'
        }}
        >
          <div>
            <label style={{ fontWeight: 500, marginRight: 8 }}>Select Tag:</label>
            <Select
              placeholder="Select a tag"
              value={selectedTagId}
              onChange={setSelectedTagId}
              style={{ width: 300 }}
              showSearch
              optionFilterProp="children"
              loading={tagValuesInFlight}
            >
              {tagValues.map(tv => (
                <Select.Option key={tv.id} value={tv.id}>{tv.name}</Select.Option>
              ))}
            </Select>
          </div>
          <div>
            <label style={{ fontWeight: 500, marginRight: 8 }}>Start Date:</label>
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="DD-MM-YYYY HH:mm"
              placeholder="No start date"
              value={startDate}
              onChange={setStartDate}
              disabledDate={val => setUpperDateRange(val, expiryDate)}
            />
          </div>
          <div>
            <label style={{ fontWeight: 500, marginRight: 8 }}>Expiry Date:</label>
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="DD-MM-YYYY HH:mm"
              placeholder="No expiry date"
              value={expiryDate}
              onChange={setExpiryDate}
              disabledDate={val => setLowerDateRange(val, startDate)}
            />
          </div>
        </div>
      </div>

      {selectedTagId && (
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <Transfer
            canAdmin={canAdmin}
            reducer={{
              destination: REDUCER_KEY.assignedTagUsers,
              source: REDUCER_KEY.unAssignedTagUsers
            }}
            objectId={selectedTagId}
            config={{
              ...config,
              commands: {
                disableAdd: !canAdmin,
                disableRemove: !canAdmin
              },
              source: {
                ...config.source,
                filters: { tag_id__ne: [selectedTagId] }
              },
              destination: {
                ...config.destination,
                filters: { tag_id: [selectedTagId] }
              }
            }}
            onPrepareSourceData={handleAssignData}
            onPrepareDestinationData={handleUnassignData}
            onAssignSuccess={handleAssignResponse}
            onUnassignSuccess={handleUnassignResponse}
            permissions={permissions}
          />
        </div>
      )}
    </div>
  );
};

TaggedItems.propTypes = {
  objectId: PropTypes.number,
  permissions: PropTypes.object.isRequired,
  tagValuesData: PropTypes.object,
  tagValuesInFlight: PropTypes.bool,
  tagValuesIsDirty: PropTypes.bool,
  loadTagValues: PropTypes.func.isRequired,
  dispatchShowValidationBlock: PropTypes.func.isRequired
};

TaggedItems.defaultProps = {
  objectId: null,
  tagValuesData: { results: [] },
  tagValuesInFlight: false,
  tagValuesIsDirty: false
};

export default connect(mapStateToProps, mapDispatchToProps)(TaggedItems);
